import moment from 'moment';

export type CalendarEvent = {
  id: string;
  date: string;          // ISO date, day granularity (YYYY-MM-DD)
  typeKey: string;
  title: string;         // primary human text (NOT the type label)
  badge?: string;
  badgeTone?: 'danger' | 'warn' | 'muted';
};

export type MonthCell = {
  dayNo: number;
  outside: boolean;      // spill day from an adjacent month
  isoDate: string | null; // null when outside
  col: number;           // 0..COLS-1; col % 7 is the weekday (Mon=0)
  week: number;          // ISO week number of this cell's actual date (incl. spill days)
};

export const COLS = 37; // max leading pad (6) + longest month (31)

const mondayIdx = (jsDay: number) => (jsDay + 6) % 7; // JS Sun=0 -> Mon=0

export const buildMonthCells = (year: number, month: number): MonthCell[] => {
  const lead = mondayIdx(new Date(year, month, 1).getDay());
  const dim = new Date(year, month + 1, 0).getDate();
  const cells: MonthCell[] = [];
  for (let i = 0; i < COLS; i++) {
    const dayIndex = i - lead; // 0-based day-of-month
    const inMonth = dayIndex >= 0 && dayIndex < dim;
    const dayNo = inMonth
      ? dayIndex + 1
      : dayIndex < 0
        ? new Date(year, month, 0).getDate() + dayIndex + 1 // prev month tail
        : dayIndex - dim + 1;                               // next month head
    // Actual date of this cell (JS Date normalizes negative / overflow day indices),
    // so the ISO week is correct even for adjacent-month spill cells.
    const cellDate = new Date(year, month, dayIndex + 1);
    cells.push({
      dayNo,
      outside: !inMonth,
      isoDate: inMonth
        ? `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNo).padStart(2, '0')}`
        : null,
      col: i,
      week: moment(cellDate).isoWeek(),
    });
  }
  return cells;
};

// True when a month-aligned window of `monthCount` months starting at `start` ends before the month
// containing `today` — i.e. everything it can show is already in the past.
//
// Used to decide whether a persisted calendar range is still worth restoring. A range the user
// stepped to months ago reopens on a window with nothing in it, which reads as a broken page rather
// than as remembered state. Windows in the FUTURE are left alone: those are deliberate planning,
// and the "Idag" button is one click away.
export const isWindowPast = (
  start: { year: number; month: number },
  monthCount: number,
  today: { year: number; month: number },
): boolean => (start.year * 12 + start.month) + monthCount - 1 < today.year * 12 + today.month;

export const MAX_DOTS = 3;
// Bounds the "+N" label at two digits so the row's widest form is provable. A day with more than
// 99 activities is not a real scenario; this exists to keep the width arithmetic total.
const COUNT_LIMIT = 99;

// What the marks row actually renders. The row must fit the narrowest cell the grid guarantees —
// roughly 23px at the table's 1040px min-width — so its widest form is bounded by construction
// rather than clipped by CSS afterwards:
//   • nothing hidden  -> up to MAX_DOTS dots      (3 × 6px + 2 × 1px gap = 20px)
//   • something hidden -> ONE dot plus the count  (6px + 1px + a two-digit label ≈ 22px)
// Two dots beside a two-digit count is ~29px and does not fit: that combination is what used to
// clip "+12" down to a visible "+1", telling the user 12 hidden activities were 1.
export const dayMarks = (typeKeys: string[]): { dots: string[]; more: number } => {
  const { types } = dayDots(typeKeys);
  const hidden = typeKeys.length - types.length > 0;
  const dots = hidden ? types.slice(0, 1) : types;
  return { dots, more: Math.min(typeKeys.length - dots.length, COUNT_LIMIT) };
};

// One dot per distinct activity type (colour), capped at 3. `overflow` is the number of
// activities NOT represented by a shown dot = total activities minus dots shown — so extra
// activities within an already-dotted category are counted too (e.g. 3×A + 2×B -> [A,B] +3).
export const dayDots = (typeKeys: string[]): { types: string[]; overflow: number } => {
  const distinct: string[] = [];
  for (const k of typeKeys) if (!distinct.includes(k)) distinct.push(k);
  const types = distinct.slice(0, 3);
  return { types, overflow: typeKeys.length - types.length };
};

// Workload heat PER DAY, relative to the busiest day in the given (already range-windowed)
// events. Returns an isoDate -> level (1..4) map; only days that have events appear (days
// with none are absent -> level 0 -> uncolored). The busiest day maps to 4 (darkest) and the
// rest scale proportionally across the full legend, so it uses all 5 steps rather than one
// colour. Because the caller passes the windowed events, the scale re-bases when the shown
// timeframe changes (3-month closed view vs. 12-month expanded view).
//
// The level is also capped by the day's own count. Scaling purely against the busiest day means
// the level says nothing on its own: with one activity per day the busiest day has 1, every day
// divides out to 4, and a completely quiet quarter renders as solid "Högt tryck". Capping keeps
// a 1-activity day at level 1 and a 2-activity day at 2, so the darkest bands need real volume
// behind them. It only binds while the busiest day is under 4 activities — above that the
// proportional scale is the smaller of the two and the full legend is used as before.
export const dayLoadLevels = (events: CalendarEvent[]): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const e of events) counts.set(e.date, (counts.get(e.date) ?? 0) + 1);
  let max = 0;
  counts.forEach((c) => { if (c > max) max = c; });
  const levels = new Map<string, number>();
  counts.forEach((c, d) => levels.set(d, max ? Math.min(Math.ceil((c / max) * 4), c) : 0));
  return levels;
};

// Busiest ISO week in the given (already range-windowed) events — i.e. the peak of whatever
// is currently visible (3-month closed view or 12-month expanded view). Returns the week with
// the most activities; on a tie the chronologically earliest such week (so it stays deterministic
// and doesn't vanish when several weeks tie — common in the 12-month view). Needs >= 2 activities
// in that week to count as "pressure", else null.
//
// Buckets by ISO week-year AND week number. A week number alone does not identify a week: a
// 12-month window spans ~52.2 weeks, which is longer than a 52-week ISO year, so most such windows
// contain the same number twice — e.g. Aug 2027–Jul 2028 holds w30 of 2027 and w30 of 2028. Keying
// on the bare number merged those counts and could report a week that was never busy.
//
// Returns the ISO week-year alongside the number. Bucketing fixed the arithmetic but the label was
// still just "v.30", which does not identify a week either — in a window holding two w30s the badge
// named one of them and the reader could not tell which. The consumer decides whether to show the
// year; it only matters when the visible window straddles two ISO week-years.
export type PeakWeek = { week: number; year: number };

export const peakWeek = (events: CalendarEvent[]): PeakWeek | null => {
  if (!events.length) return null;
  // year * 100 + week — a single sortable number whose ordering is chronological.
  const counts = new Map<number, number>();
  for (const e of events) {
    const m = moment(e.date, 'YYYY-MM-DD');
    const key = m.isoWeekYear() * 100 + m.isoWeek();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  let max = 0;
  counts.forEach((n) => { if (n > max) max = n; });
  if (max < 2) return null;
  // Comparing composite keys is a real chronological comparison; comparing bare week numbers was
  // not — it ranked w5 of January ahead of w48 of the previous December.
  let earliest = Number.MAX_SAFE_INTEGER;
  counts.forEach((n, key) => { if (n === max && key < earliest) earliest = key; });
  return { week: earliest % 100, year: Math.floor(earliest / 100) };
};

// ---- Swedish public holidays ("röda dagar") --------------------------------------------
// Movable feasts derive from Easter Sunday (Anonymous Gregorian "Computus" algorithm); fixed
// and Saturday-based ones are added directly. Returns a Set of 'YYYY-MM-DD' for the year.
const fmtDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const addDays = (d: Date, n: number): Date => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

const easterSunday = (year: number): Date => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = March, 4 = April
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};

// First Saturday on/after the given month/day.
const saturdayOnOrAfter = (year: number, month0: number, day: number): Date => {
  const d = new Date(year, month0, day);
  return addDays(d, (6 - d.getDay() + 7) % 7);
};

export const swedishHolidays = (year: number): Set<string> => {
  const easter = easterSunday(year);
  const midsummerDay = saturdayOnOrAfter(year, 5, 20); // Saturday between 20–26 Jun
  const allSaints = saturdayOnOrAfter(year, 9, 31);    // Saturday between 31 Oct – 6 Nov
  return new Set([
    new Date(year, 0, 1),      // Nyårsdagen
    new Date(year, 0, 6),      // Trettondedag jul
    addDays(easter, -2),       // Långfredag
    easter,                    // Påskdagen
    addDays(easter, 1),        // Annandag påsk
    new Date(year, 4, 1),      // Första maj
    addDays(easter, 39),       // Kristi himmelsfärds dag
    addDays(easter, 49),       // Pingstdagen
    new Date(year, 5, 6),      // Sveriges nationaldag
    addDays(midsummerDay, -1), // Midsommarafton
    midsummerDay,              // Midsommardagen
    allSaints,                 // Alla helgons dag
    new Date(year, 11, 24),    // Julafton
    new Date(year, 11, 25),    // Juldagen
    new Date(year, 11, 26),    // Annandag jul
    new Date(year, 11, 31),    // Nyårsafton
  ].map(fmtDate));
};
